<script setup lang="ts">
  import { computed, defineAsyncComponent, ref } from 'vue';
  import { useRouter } from 'vue-router';

  import type { CreditMemo, EmailThread, Invoice, PartnerBase } from '@stuut/api/models';
  import { CreditMemoStatus, InboxCategory, InvoiceStatus } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { toast } from '@ui/components/sonner';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import LinkEmailDialog from '@/components/email-senders/LinkEmailDialog.vue';
  import { routeToInbox } from '@/utils/routeToInbox';

  const props = withDefaults(defineProps<Props>(), {
    inboxRedirect: false,
  });

  const emit = defineEmits<{
    (e: 'updated'): void;
  }>();

  const EmailThreadDrawer = defineAsyncComponent(
    () => import('@/components/email-threads/EmailThreadDrawer.vue'),
  );

  interface Props {
    buttonType?: string;
    creditMemos?: CreditMemo[];
    hideButtonText?: boolean;
    inboxRedirect?: boolean;
    invoices?: Invoice[];
    loading?: boolean;
    partner?: PartnerBase;
    primary?: boolean;
  }

  const router = useRouter();
  const linkEmailDialogRef = ref();
  const isSubmitting = ref(false);

  const showEmailDrawer = ref();
  const sheetThread = ref();

  const closeSheetDrawer = () => {
    sheetThread.value = undefined;
    showEmailDrawer.value = false;
  };

  const openSheetDrawer = (newEmailThread: EmailThread) => {
    sheetThread.value = newEmailThread;
    showEmailDrawer.value = true;
  };

  const updateEmailThread = (updatedThread: EmailThread) => {
    if (updatedThread) sheetThread.value = updatedThread;
    emit('updated');
  };

  const isInvoiceOpen = (inv: Invoice): boolean => {
    return inv.status === InvoiceStatus.OPEN || inv.status === InvoiceStatus.PARTIALLY_PAID;
  };

  const isCreditMemoOpen = (cm: CreditMemo): boolean => {
    return cm.status === CreditMemoStatus.OPEN;
  };

  const disabledReason = computed(() => {
    if (props.creditMemos) {
      if (!props.creditMemos.length) return 'Must select a Credit Memo to send an email';
      const partnerIds = new Set(props.creditMemos.map((cm) => cm.partner_id));
      if (partnerIds.size > 1) {
        return 'All selected credit memos must be from the same partner';
      }
      if (props.creditMemos.some((cm) => !isCreditMemoOpen(cm))) {
        return props.creditMemos.length === 1
          ? "Credit memo status must be 'Open'"
          : "All selected Credit Memos must be in an 'Open' status";
      }
      return undefined;
    }
    if (props.invoices && !props.invoices.length) {
      return 'Must select an Invoice to send an email';
    }
    if (props.invoices && props.invoices.some((inv) => !isInvoiceOpen(inv))) {
      return props.invoices.length === 1
        ? "Invoice status must be 'Open'"
        : "All selected Invoices must be in an 'Open' status";
    }
    return undefined;
  });

  const createEmail = async () => {
    if (isSubmitting.value) return;

    try {
      isSubmitting.value = true;

      const draftEmail = await stuut.emails.create({
        credit_memos: props.creditMemos?.map((cm) => cm.id),
        invoices: props.invoices?.map((inv) => inv.id),
        options: { use_llm: false },
        partner: props.partner?.id,
      });

      if (props.inboxRedirect) {
        routeToInbox({
          entityId: draftEmail.thread_id,
          inboxCategory: InboxCategory.DRAFTS,
          router,
          type: 'emails',
        });

        toast.success('Draft email has been generated.');
      } else {
        const emailThread = await stuut.emailThreads.retrieve(draftEmail.thread_id);

        sheetThread.value = emailThread;
        openSheetDrawer(emailThread);
      }
    } catch (err: any) {
      toast.error(err.clientMessage);
    } finally {
      isSubmitting.value = false;
    }
  };
</script>

<template>
  <Tooltip>
    <TooltipTrigger as-child>
      <Button
        v-bind="$attrs"
        :disabled="!!disabledReason"
        size="sm"
        :loading="isSubmitting"
        @click="createEmail"
      >
        <i class="fa fa-envelope" />
        <span :class="[hideButtonText ? 'hidden' : '']"> Email </span>
        <i v-if="props.loading" class="fa fa-spinner-third animate-spin" />
      </Button>
    </TooltipTrigger>

    <TooltipContent v-if="disabledReason">
      <p>
        {{ disabledReason }}
      </p>
    </TooltipContent>

    <LinkEmailDialog ref="linkEmailDialogRef" />

    <EmailThreadDrawer
      v-if="sheetThread"
      v-model:open="showEmailDrawer"
      :thread="sheetThread"
      @close="closeSheetDrawer"
      @updated="updateEmailThread"
      @deleted="closeSheetDrawer"
    />
  </Tooltip>
</template>

<style scoped></style>
